import React, { useState, useEffect, useRef } from "react";

import {
  FaTimes,
  FaBuilding,
  FaFileInvoice,
  FaPlus,
  FaTrash,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaUniversity,
  FaIdCard,
  FaUpload,
  FaFile,
  FaSpinner,
} from "react-icons/fa";
import { SearchableSelect, SelectWithSearch } from "../../utils/select-with-search";
import axiosInstance from "../../libs/axios";
import { countries } from "../../utils/countries";
import { LocationAutocomplete } from "../../utils/location-autocomplete";
import { CloseModal } from "../sites/closemodal";
import { uploadFilesWithPresignedPayload } from "../../libs/presigned-upload";
import toast from "react-hot-toast";

interface VendorRepresentative {
  contact_person: string;
  contact_number: string;
  country_code: string;
  contact_number_with_code?: string;
  designation_id: number | "";
  representative_location: string;
  email?: string;
}

interface VendorDocument {
  name: string;
  url: string;
  size?: number;
  mime_type?: string;
}

interface VendorFormData {
  vendor_full_name: string;
  vendor_short_name: string;
  vendor_code?: string;
  address: string;
  gst_number: string;
  unregistered_gst: boolean;
  state_circle_id: number | "";
  vendor_type: string;
  status?: string;
  pan_number: string;
  bank_account_number: string;
  bank_ifsc_code: string;
  bank_name: string;
  bank_address: string;
  representatives?: VendorRepresentative[];
  documents?: VendorDocument[];
  project_ids?: number[];
  activity_ids?: number[];
}

interface AddVendorOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: VendorFormData) => void;
  loading?: boolean;
}

export interface DesignationOption {
  value: number;
  label: string;
}

export interface CountryOption {
  value: string;
  label: string;
  flag: string;
}

const initialFormData: VendorFormData = {
  vendor_full_name: "",
  vendor_short_name: "",
  vendor_code: "",
  address: "",
  gst_number: "",
  unregistered_gst: false,
  state_circle_id: "",
  vendor_type: "FREELANCE",
  status: "active",
  pan_number: "",
  bank_account_number: "",
  bank_ifsc_code: "",
  bank_name: "",
  bank_address: "",
  representatives: [],
  documents: [],
  project_ids: [],
  activity_ids: [],
};

const initialRepresentative: VendorRepresentative = {
  contact_person: "",
  contact_number: "",
  country_code: "+91",
  designation_id: "",
  representative_location: "",
  email: "",
};

export function AddVendorOffcanvas({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}: AddVendorOffcanvasProps) {
  const [formData, setFormData] = useState<VendorFormData>(initialFormData);
  const [representatives, setRepresentatives] = useState<VendorRepresentative[]>([]);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [representativeErrors, setRepresentativeErrors] = useState<Record<number, Partial<VendorRepresentative>>>({});

  const [selectedStateCircle, setSelectedStateCircle] = useState<any | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<{ value: string | number; label: string }[]>([]);
  const [projects, setProjects] = useState<{ id: number; project_name: string }[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [activities, setActivities] = useState<{ id: number; activity_name: string }[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Document upload state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Designation state
  const [designationOptions, setDesignationOptions] = useState<DesignationOption[]>([]);
  const [designationLoading, setDesignationLoading] = useState(false);
  const [designationPage, setDesignationPage] = useState(1);
  const [designationHasMore, setDesignationHasMore] = useState(true);
  const [designationSearch, setDesignationSearch] = useState("");
  const [showCloseModal, setShowCloseModal] = useState(false);

  const countryOptions: CountryOption[] = countries.map(country => ({
    value: country.code,
    label: `${country.flag} ${country.code}`,
    flag: country.flag
  }));

  const fetchDesignations = async (search: string = "", page: number = 1, append: boolean = false) => {
    setDesignationLoading(true);
    try {
      const response = await axiosInstance.get('/designations/list', {
        params: { search: search.trim() || undefined, page, limit: 20, status: 'active' }
      });
      if (response.data.success) {
        const newDesignations = response.data.data.designations.map((d: any) => ({
          value: d.id, label: d.name
        }));
        if (append) {
          setDesignationOptions(prev => [...prev, ...newDesignations]);
        } else {
          setDesignationOptions(newDesignations);
        }
        setDesignationHasMore(response.data.data.pagination.has_next_page);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    } finally {
      setDesignationLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDesignations();
      fetchProjects();
      fetchActivities();
    } else {
      setShowCloseModal(false);
    }
  }, [isOpen]);

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const response = await axiosInstance.get('/projects/list', { params: { page: 1, limit: 200, status: 'ACTIVE' } });
      if (response.data.success) setProjects(response.data.data.projects || []);
    } catch { } finally { setProjectsLoading(false); }
  };

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    try {
      const response = await axiosInstance.get('/activities/list', { params: { page: 1, limit: 200 } });
      if (response.data.success) setActivities(response.data.data.activities || []);
    } catch { } finally { setActivitiesLoading(false); }
  };

  const handleDesignationSearch = (search: string) => {
    setDesignationSearch(search);
    setDesignationPage(1);
    fetchDesignations(search, 1, false);
  };

  const loadMoreDesignations = () => {
    if (designationHasMore && !designationLoading) {
      const nextPage = designationPage + 1;
      setDesignationPage(nextPage);
      fetchDesignations(designationSearch, nextPage, true);
    }
  };

  const handleStateCircleChange = (option: any | null) => {
    setSelectedStateCircle(option);
    setFormData(prev => ({ ...prev, state_circle_id: option ? Number(option.value) : "" }));
    if (errors.state_circle_id) {
      setErrors(prev => ({ ...prev, state_circle_id: null }));
    }
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "vendor_full_name":
        if (!value.trim()) return "Full name is required";
        if (value.trim().length < 2) return "Full name must be at least 2 characters";
        if (value.trim().length > 100) return "Full name must be less than 100 characters";
        break;
      case "vendor_short_name":
        if (!value.trim()) return "Short name is required";
        if (value.trim().length < 2) return "Short name must be at least 2 characters";
        if (value.trim().length > 50) return "Short name must be less than 50 characters";
        break;
      case "address":
        if (!value.trim()) return "Address is required";
        if (value.trim().length < 10) return "Address must be at least 10 characters";
        if (value.trim().length > 500) return "Address must be less than 500 characters";
        break;
      case "gst_number":
        if (value.trim() && !formData.unregistered_gst) {
          if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.trim())) return "Invalid GST number format";
        }
        break;
      case "state_circle_id":
        if (value === "" || Number.isNaN(Number(value))) return "State circle is required";
        break;
      default:
        break;
    }
    return null;
  };

  const validateRepresentative = (rep: VendorRepresentative) => {
    const repErrors: Partial<VendorRepresentative> = {};
    if (!rep.contact_person.trim()) repErrors.contact_person = "Contact person is required";
    else if (rep.contact_person.trim().length < 2) repErrors.contact_person = "Name must be at least 2 characters";
    if (!rep.contact_number.trim()) repErrors.contact_number = "Contact number is required";
    else if (rep.contact_number.trim().length < 6 || rep.contact_number.trim().length > 15) repErrors.contact_number = "Enter a valid phone number";
    if (rep.designation_id === "" || rep.designation_id === null) repErrors.designation_id = "Designation is required" as any;
    if (!rep.representative_location.trim()) repErrors.representative_location = "Location is required";
    if (rep.email && rep.email.trim() && !/^\S+@\S+\.\S+$/.test(rep.email.trim())) repErrors.email = "Enter a valid email";
    return repErrors;
  };

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: name === "state_circle_id" ? (value ? Number(value) : "") : value,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (name: string, value: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const toggleUnregisteredGst = (checked: boolean) => {
    setFormData(prev => ({ ...prev, unregistered_gst: checked, gst_number: checked ? "" : prev.gst_number }));
    if (checked) setErrors(prev => ({ ...prev, gst_number: null }));
  };

  // ===== Document upload handlers =====
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = "";
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ===== Representative handlers =====
  const addRepresentativeField = () => {
    setRepresentatives(prev => [...prev, { ...initialRepresentative }]);
  };

  const removeRepresentativeField = (index: number) => {
    setRepresentatives(prev => prev.filter((_, i) => i !== index));
    setRepresentativeErrors(prev => {
      const nextErrors = { ...prev };
      delete nextErrors[index];
      return nextErrors;
    });
  };

  const updateRepresentative = (index: number, field: keyof VendorRepresentative, value: string | number) => {
    setRepresentatives(prev => prev.map((rep, i) => (i === index ? { ...rep, [field]: value } : rep)));
    if (representativeErrors[index]?.[field]) {
      setRepresentativeErrors(prev => ({ ...prev, [index]: { ...prev[index], [field]: undefined } }));
    }
  };

  const updateRepresentativeCountryCode = (index: number, option: CountryOption | null) => {
    if (option) updateRepresentative(index, "country_code", option.value);
  };

  const updateRepresentativeDesignation = (index: number, option: DesignationOption | null) => {
    updateRepresentative(index, "designation_id", option ? option.value : "");
  };

  const validateRepresentatives = () => {
    const errors: Record<number, Partial<VendorRepresentative>> = {};
    let hasErrors = false;
    representatives.forEach((rep, index) => {
      const repErrors = validateRepresentative(rep);
      if (Object.keys(repErrors).length > 0) {
        errors[index] = repErrors;
        hasErrors = true;
      }
    });
    setRepresentativeErrors(errors);
    return !hasErrors;
  };

  const validateForm = () => {
    const newErrors: Record<string, string | null> = {};
    let isValid = true;

    const fieldsToValidate = ["vendor_full_name", "vendor_short_name", "address", "state_circle_id", "gst_number"];

    fieldsToValidate.forEach((field) => {
      const value = formData[field as keyof VendorFormData];
      const error = validateField(field, String(value ?? ""));
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    const touchedFields: Record<string, boolean> = {
      vendor_full_name: true,
      vendor_short_name: true,
      address: true,
      state_circle_id: true,
      gst_number: true,
    };
    setTouched(touchedFields);

    if (!validateRepresentatives()) isValid = false;
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setUploading(true);
    let uploadedDocs: VendorDocument[] = [];
    try {
      if (pendingFiles.length > 0) {
        const results = await uploadFilesWithPresignedPayload(pendingFiles, { folderName: "vendors/documents" });
        uploadedDocs = results.map(r => ({
          name: r.fileName,
          url: r.fileUrl,
          size: r.fileSize,
          mime_type: r.mimeType,
        }));
      }
    } catch (err) {
      toast.error("Failed to upload documents. Please try again.");
      setUploading(false);
      return;
    }
    setUploading(false);

    const payload: VendorFormData = {
      ...formData,
      vendor_full_name: formData.vendor_full_name.trim(),
      vendor_short_name: formData.vendor_short_name.trim(),
      vendor_code: formData.vendor_code?.trim() || undefined,
      address: formData.address.trim(),
      gst_number: formData.unregistered_gst ? "" : formData.gst_number.trim().toUpperCase(),
      state_circle_id: Number(formData.state_circle_id),
      status: formData.status || "active",
      pan_number: formData.pan_number.trim(),
      bank_account_number: formData.bank_account_number.trim(),
      bank_ifsc_code: formData.bank_ifsc_code.trim().toUpperCase(),
      bank_name: formData.bank_name.trim(),
      bank_address: formData.bank_address.trim(),
      documents: uploadedDocs,
      project_ids: selectedProjects.map((p: any) => Number(p.value)),
      activity_ids: selectedActivities.map((a: any) => Number(a.value)),
      representatives: representatives.length
        ? representatives.map(rep => ({
            ...rep,
            contact_person: rep.contact_person.trim(),
            contact_number: rep.contact_number.trim(),
            country_code: rep.country_code,
            designation_id: Number(rep.designation_id),
            representative_location: rep.representative_location.trim(),
            email: rep.email?.trim() || undefined,
          }))
        : [],
    };

    onSubmit(payload);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !loading && !uploading) {
        setShowCloseModal(true);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, loading, uploading]);

  const requestClose = () => {
    if (!loading && !uploading) setShowCloseModal(true);
  };

  const confirmClose = () => {
    if (!loading && !uploading) {
      setShowCloseModal(false);
      setFormData({ ...initialFormData });
      setRepresentatives([]);
      setErrors({});
      setTouched({});
      setRepresentativeErrors({});
      setSelectedStateCircle(null);
      setSelectedProjects([]);
      setSelectedActivities([]);
      setDesignationOptions([]);
      setDesignationPage(1);
      setDesignationSearch("");
      setPendingFiles([]);
      onClose();
    }
  };

  if (!isOpen) return null;

  const isSubmitDisabled =
    loading ||
    uploading ||
    !formData.vendor_full_name.trim() ||
    !formData.vendor_short_name.trim() ||
    !formData.address.trim() ||
    formData.state_circle_id === "";

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={requestClose} />
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add Vendor</h2>
            <p className="text-sm text-gray-600 mt-1">Create a new vendor along with representative details</p>
          </div>
          <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded transition-colors" disabled={loading || uploading}>
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="h-full overflow-y-auto pb-32">
          <div className="p-6 space-y-6">
            {/* ===== Vendor Information ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-6">
              <div className="flex items-center gap-2">
                <FaBuilding className="text-blue-800" />
                <h3 className="text-lg font-medium text-gray-900">Vendor Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_full_name}
                    onChange={(e) => handleInputChange("vendor_full_name", e.target.value)}
                    onBlur={(e) => handleBlur("vendor_full_name", e.target.value)}
                    placeholder="Enter vendor full name"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.vendor_full_name && touched.vendor_full_name ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    disabled={loading}
                  />
                  {errors.vendor_full_name && touched.vendor_full_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.vendor_full_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_short_name}
                    onChange={(e) => handleInputChange("vendor_short_name", e.target.value)}
                    onBlur={(e) => handleBlur("vendor_short_name", e.target.value)}
                    placeholder="Enter vendor short name"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.vendor_short_name && touched.vendor_short_name ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    disabled={loading}
                  />
                  {errors.vendor_short_name && touched.vendor_short_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.vendor_short_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vendor Code
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_code}
                    onChange={(e) => handleInputChange("vendor_code", e.target.value)}
                    placeholder="e.g. VND-001"
                    maxLength={50}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    disabled={loading}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    onBlur={(e) => handleBlur("address", e.target.value)}
                    rows={3}
                    placeholder="Enter complete address"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.address && touched.address ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    disabled={loading}
                  />
                  {errors.address && touched.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>

                {/* GST section */}
                <div className="md:col-span-2 space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.unregistered_gst}
                        onChange={(e) => toggleUnregisteredGst(e.target.checked)}
                        disabled={loading}
                        className="rounded border-gray-300 text-blue-800 focus:ring-blue-500"
                      />
                      Unregistered GST
                    </label>
                    {formData.unregistered_gst && (
                      <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                        GST number will not be collected
                      </span>
                    )}
                  </div>

                  {!formData.unregistered_gst && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GST Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaFileInvoice className="w-4 h-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={formData.gst_number}
                          onChange={(e) => handleInputChange("gst_number", e.target.value)}
                          onBlur={(e) => handleBlur("gst_number", e.target.value)}
                          placeholder="e.g. 27AAAAA0000A1Z5"
                          maxLength={15}
                          className={`w-full pl-10 pr-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono ${errors.gst_number && touched.gst_number ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                          disabled={loading}
                        />
                      </div>
                      {errors.gst_number && touched.gst_number && (
                        <p className="mt-1 text-sm text-red-600">{errors.gst_number}</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State Circle <span className="text-red-500">*</span>
                  </label>
                  <SelectWithSearch
                    value={selectedStateCircle}
                    onChange={handleStateCircleChange}
                    placeholder="Select state"
                    apiEndpoint="/state-circle/list"
                    dataKey="states"
                    labelKey="state_name"
                    displayFormat={(item) => `${item.state_name}`}
                  />
                  {errors.state_circle_id && touched.state_circle_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.state_circle_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vendor Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.vendor_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, vendor_type: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  >
                    <option value="FREELANCE">Freelance</option>
                    <option value="FREELANCE_GROUP">Freelance Group</option>
                    <option value="FIRM">Firm</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.status === "active"}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? "active" : "SUSPENDED" }))}
                    disabled={loading}
                    className="rounded border-gray-300 text-blue-800 focus:ring-blue-500"
                  />
                  Set as active
                </label>
              </div>
            </div>

            {/* ===== Bank Details ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-5">
              <div className="flex items-center gap-2">
                <FaUniversity className="text-blue-800" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Bank Details</h3>
                  <p className="text-xs text-gray-500 mt-0.5">All fields optional</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PAN Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaIdCard className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.pan_number}
                      onChange={(e) => handleInputChange("pan_number", e.target.value.toUpperCase())}
                      placeholder="e.g. ABCDE1234F"
                      maxLength={10}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_number}
                    onChange={(e) => handleInputChange("bank_account_number", e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="Enter account number"
                    maxLength={20}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank IFSC Code
                  </label>
                  <input
                    type="text"
                    value={formData.bank_ifsc_code}
                    onChange={(e) => handleInputChange("bank_ifsc_code", e.target.value.toUpperCase())}
                    placeholder="e.g. SBIN0001234"
                    maxLength={11}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange("bank_name", e.target.value)}
                    placeholder="e.g. State Bank of India"
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Address
                  </label>
                  <textarea
                    value={formData.bank_address}
                    onChange={(e) => handleInputChange("bank_address", e.target.value)}
                    rows={2}
                    placeholder="Enter bank branch address"
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* ===== Project & Activity ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-5">
              <div className="flex items-center gap-2">
                <FaBuilding className="text-blue-800" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Project & Activity</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Optional associations</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Projects</label>
                  <SearchableSelect
                    value={selectedProjects}
                    isMulti={true}
                    showSelectAllVisible={true}
                    onChange={(options: any) => setSelectedProjects(options || [])}
                    options={projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="Search and select projects"
                    disabled={loading}
                    isLoading={projectsLoading}
                    minSearchLength={3}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activities</label>
                  <SearchableSelect
                    value={selectedActivities}
                    isMulti={true}
                    showSelectAllVisible={true}
                    onChange={(options: any) => setSelectedActivities(options || [])}
                    options={activities.map(a => ({ value: a.id, label: a.activity_name }))}
                    placeholder="Search and select activities"
                    disabled={loading}
                    isLoading={activitiesLoading}
                    minSearchLength={3}
                  />
                </div>
              </div>
            </div>

            {/* ===== Documents ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaFile className="text-blue-800" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Documents</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Upload any supporting documents (optional)</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors flex items-center gap-2"
                  disabled={loading || uploading}
                >
                  <FaUpload className="w-3 h-3" />
                  Add Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              {pendingFiles.length === 0 ? (
                <p className="text-sm text-gray-500">No documents added yet.</p>
              ) : (
                <div className="space-y-2">
                  {pendingFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FaFile className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePendingFile(index)}
                        className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                        disabled={loading || uploading}
                      >
                        <FaTimes className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ===== Representatives ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaUser className="text-blue-800" />
                  <h3 className="text-lg font-medium text-gray-900">Representatives</h3>
                </div>
                <button
                  type="button"
                  onClick={addRepresentativeField}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors flex items-center gap-2"
                  disabled={loading}
                >
                  <FaPlus className="w-3 h-3" />
                  Add Representative
                </button>
              </div>

              {representatives.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No representatives added yet. Click "Add Representative" to include contact details for this vendor.
                </p>
              ) : (
                <div className="space-y-4">
                  {representatives.map((rep, index) => (
                    <div key={index} className="border border-gray-200 rounded p-4 bg-white">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-sm font-medium text-gray-800">Representative #{index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeRepresentativeField(index)}
                          className="text-red-600 hover:text-red-700 text-xs font-medium flex items-center gap-1"
                          disabled={loading}
                        >
                          <FaTrash className="w-3 h-3" />
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Person <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaUser className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              value={rep.contact_person}
                              onChange={(e) => updateRepresentative(index, "contact_person", e.target.value)}
                              placeholder="Enter contact person's name"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_person ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.contact_person && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.contact_person}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Number <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-2">
                            <div className="w-32">
                              <SearchableSelect
                                value={countryOptions.find(c => c.value === rep.country_code) || null}
                                onChange={(option: any) => updateRepresentativeCountryCode(index, option)}
                                options={countryOptions}
                                placeholder="Code"
                                disabled={loading}
                              />
                            </div>
                            <div className="flex-1 relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaPhone className="w-3 h-3 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                value={rep.contact_number}
                                onChange={(e) => updateRepresentative(index, "contact_number", e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="Enter contact number"
                                className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_number ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                                disabled={loading}
                              />
                            </div>
                          </div>
                          {representativeErrors[index]?.contact_number && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.contact_number}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaEnvelope className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="email"
                              value={rep.email ?? ""}
                              onChange={(e) => updateRepresentative(index, "email", e.target.value)}
                              placeholder="Enter email (optional)"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.email ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.email && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.email}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Designation <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            value={designationOptions.find(d => d.value === rep.designation_id) || null}
                            onChange={(option: any) => updateRepresentativeDesignation(index, option)}
                            options={designationOptions}
                            placeholder="Select designation"
                            disabled={loading}
                            isLoading={designationLoading}
                            onSearch={handleDesignationSearch}
                            error={representativeErrors[index]?.designation_id as string}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Location <span className="text-red-500">*</span>
                          </label>
                          <LocationAutocomplete
                            value={rep.representative_location}
                            onChange={(value) => updateRepresentative(index, "representative_location", value)}
                            placeholder="Search and select location"
                            disabled={loading}
                            error={representativeErrors[index]?.representative_location}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 right-0 w-full sm:max-w-4xl p-6 border-t border-gray-200 bg-white flex justify-end gap-3">
          <button
            type="button"
            onClick={requestClose}
            disabled={loading || uploading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
          >
            {uploading ? (
              <>
                <FaSpinner className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              <>
                <FaBuilding className="w-4 h-4" />
                Create Vendor
              </>
            )}
          </button>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
}
